import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useCreateAssistant, useUpdateAssistant } from '@/hooks/useAssistants';
import { useChannels } from '@/hooks/useChannels';
import type { Assistant } from '@/types/api.types';
import { toast } from 'sonner';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { asApiError } from '@/utils/error.util';

interface AssistantConfigModalProps {
  assistant: Assistant | null;
  onClose: () => void;
}

export const AssistantConfigModal = ({ assistant, onClose }: AssistantConfigModalProps) => {
  const createMutation = useCreateAssistant();
  const updateMutation = useUpdateAssistant();
  const { data: channels } = useChannels();

  const isEditing = !!assistant;
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const [name, setName] = useState(assistant?.users_assistant_account?.display_name || '');
  const [slug, setSlug] = useState(assistant?.slug || '');
  const [description, setDescription] = useState(assistant?.description || '');
  const [systemPrompt, setSystemPrompt] = useState(assistant?.system_prompt || '');
  const [model, setModel] = useState(assistant?.model || 'gpt-5-mini');
  const [temperature, setTemperature] = useState(assistant?.temperature ?? 0.7);
  const [maxContextMessages, setMaxContextMessages] = useState(assistant?.max_context_messages ?? 10);
  const [channelScope, setChannelScope] = useState<'all' | 'selected'>(assistant?.channel_scope || 'all');
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>(
    assistant?.assistant_channels?.map((ac) => ac.channels.id) || []
  );
  const [enableWebSearch, setEnableWebSearch] = useState(assistant?.enable_web_search ?? false);
  const [dmAccess, setDmAccess] = useState(assistant?.dm_access ?? true);
  const [enabled, setEnabled] = useState(assistant?.enabled ?? true);

  // Auto-generate slug from name while creating (adjust state during render on change)
  const [slugSourceName, setSlugSourceName] = useState(name);
  if (name !== slugSourceName) {
    setSlugSourceName(name);
    if (!isEditing && name) {
      setSlug(name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''));
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !slug.trim()) {
      toast.error('Името и slug са задължителни');
      return;
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          assistantId: assistant.id,
          data: {
            name,
            description: description || undefined,
            systemPrompt,
            model,
            temperature,
            maxContextMessages,
            channelScope,
            channelIds: channelScope === 'selected' ? selectedChannelIds : undefined,
            enabled,
            enableWebSearch,
            dmAccess,
          },
        });
        toast.success('Асистентът е обновен');
      } else {
        await createMutation.mutateAsync({
          name,
          slug,
          description: description || undefined,
          systemPrompt,
          model,
          temperature,
          maxContextMessages,
          channelScope,
          channelIds: channelScope === 'selected' ? selectedChannelIds : undefined,
          enabled,
          enableWebSearch,
          dmAccess,
        });
        toast.success('Асистентът е създаден');
      }
      onClose();
    } catch (error) {
      toast.error(asApiError(error).message);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary">
          <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            {isEditing ? 'Редактирай асистент' : 'Нов асистент'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Име
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Асистент"
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Slug <span className="text-gray-400">(използва се за @споменаване)</span>
            </label>
            <div className="flex items-center gap-1">
              <span className="text-gray-400 text-sm">@</span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="assistant"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm font-mono"
                required
                disabled={isEditing}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Описание
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="AI асистент за общи въпроси"
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Системен промпт
            </label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="You are a helpful assistant..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Модел
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
            >
              <option value="gpt-5-mini">gpt-5-mini (бърз, икономичен)</option>
              <option value="gpt-5">gpt-5 (високо качество)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Температура: {temperature.toFixed(1)}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>Точен (0.0)</span>
              <span>Креативен (1.0)</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={enableWebSearch}
              onChange={(e) => setEnableWebSearch(e.target.checked)}
              className="accent-blue-600"
              id="assistant-web-search"
            />
            <label htmlFor="assistant-web-search" className="text-sm text-gray-600 dark:text-dark-text-secondary">
              Уеб търсене
            </label>
            <span className="text-xs text-gray-400">(използва Responses API — по-бавно, но с достъп до интернет)</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Контекст (брой предишни съобщения)
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={maxContextMessages}
              onChange={(e) => setMaxContextMessages(parseInt(e.target.value) || 10)}
              className="w-24 px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Достъп до канали
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-dark-text-secondary">
                <input
                  type="radio"
                  checked={channelScope === 'all'}
                  onChange={() => setChannelScope('all')}
                  className="accent-blue-600"
                />
                Всички канали
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-dark-text-secondary">
                <input
                  type="radio"
                  checked={channelScope === 'selected'}
                  onChange={() => setChannelScope('selected')}
                  className="accent-blue-600"
                />
                Избрани канали
              </label>
            </div>

            {channelScope === 'selected' && channels && (
              <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-dark-border-primary rounded-lg p-2 space-y-1">
                {channels.map((channel) => (
                  <label key={channel.id} className="flex items-center gap-2 text-sm text-gray-600 dark:text-dark-text-secondary px-2 py-1 hover:bg-gray-50 dark:hover:bg-dark-bg-hover rounded">
                    <input
                      type="checkbox"
                      checked={selectedChannelIds.includes(channel.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedChannelIds([...selectedChannelIds, channel.id]);
                        } else {
                          setSelectedChannelIds(selectedChannelIds.filter((id) => id !== channel.id));
                        }
                      }}
                      className="accent-blue-600"
                    />
                    #{channel.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={dmAccess}
              onChange={(e) => setDmAccess(e.target.checked)}
              className="accent-blue-600"
              id="assistant-dm-access"
            />
            <label htmlFor="assistant-dm-access" className="text-sm text-gray-600 dark:text-dark-text-secondary">
              Достъп до лични съобщения
            </label>
            <span className="text-xs text-gray-400">(потребителите могат да пишат на асистента в ЛС)</span>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="accent-blue-600"
              id="assistant-enabled"
            />
            <label htmlFor="assistant-enabled" className="text-sm text-gray-600 dark:text-dark-text-secondary">
              Активен
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border-primary">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-800 dark:hover:text-dark-text-primary"
            >
              Отказ
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEditing ? 'Запази' : 'Създай'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
